import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import useSafeState from "../../../hooks/useSafeState";
import {TaxIdentity as TaxIdentityApi} from '../../../api/services/next';
import Transfer from "../../../components/shared/Transfer";

const TaxIdentityHotelsTransfer = ({
    taxIdentityId,
    taxIdentityClientId,
    value,
    onChange,
}) => {
    const [hotelOptions, setHotelOptions] = useSafeState([]);
    const [targetKeys, setTargetKeys] = useSafeState([]);
    
    const loadHotelOptions = useCallback(async (taxIdentityIdToLoad) => {
        const hotels = await TaxIdentityApi.GetCreateOrEditTaxIdentityHotelOptions({
            Id: taxIdentityIdToLoad,
        });
        setHotelOptions(hotels);
    }, [setHotelOptions]);

    const loadTaxIdentityHotels = useCallback(async (taxIdentityIdToLoad) => {
        const taxIdentity = await TaxIdentityApi.GetTaxIdentity({id: taxIdentityIdToLoad});
        setTargetKeys(taxIdentity.TaxIdentityHotels.map((tih) => tih.HotelId));
        onChange(taxIdentity.TaxIdentityHotels);
    }, [onChange, setTargetKeys]);

    useEffect(() => {
        loadHotelOptions(taxIdentityId);
        if (taxIdentityId > 0) {
            loadTaxIdentityHotels(taxIdentityId);
        } else {
            setTargetKeys([]);
            onChange([]);
        }
        // keep taxIdentityClientId in deps list to refresh hotel options when create new tax identity
    }, [loadHotelOptions, loadTaxIdentityHotels, onChange, setTargetKeys, taxIdentityClientId, taxIdentityId]);

    useEffect(() => {
        setTargetKeys(value.map((taxIdentityHotel) => taxIdentityHotel.HotelId));
    }, [value, setTargetKeys]);

    const searchSelector = useCallback((hotel, searchTerm) => hotel.Name?.toLowerCase().includes(searchTerm?.toLowerCase()), []);

    const handleTaxIdentityHotelsChange = useCallback((keys) => {
        setTargetKeys(keys);
        onChange(keys.map((key) => ({
            TaxIdentityId: taxIdentityId,
            HotelId: +key,
        })));
    }, [taxIdentityId, onChange, setTargetKeys]);

    const keySelector = useCallback((hotel) => hotel.Id, []);
    const itemRender = useCallback((hotel) => `${hotel.Name}`, []);

    return (
        <Transfer
            items={hotelOptions}
            itemKeySelector={keySelector}
            itemRender={itemRender}
            targetKeys={targetKeys}
            hasSearch
            searchSelector={searchSelector}
            hasSelectAll
            onTargetKeysChange={handleTaxIdentityHotelsChange}
        />
    );
};

TaxIdentityHotelsTransfer.propTypes = {
    taxIdentityId: PropTypes.number,
    taxIdentityClientId: PropTypes.string,
    value: PropTypes.arrayOf(PropTypes.shape({})),
    onChange: PropTypes.func.isRequired,
};

TaxIdentityHotelsTransfer.defaultProps = {
    taxIdentityId: 0,
    taxIdentityClientId: '',
    value: [],
}

export default TaxIdentityHotelsTransfer;